import { useQuery } from '@tanstack/react-query';
import { listInvitations, type AdminInvitation, type Paginated } from '@/src/infrastructure/lib/api';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { EmptyState, ErrorState } from '../components/EmptyState';

// Reuses the existing /admin/invitations endpoint and filters client-side by
// the user's email — keeps the integration light and avoids a new endpoint.
export function InvitationsTab({ userId }: { userId: string }) {
  const { data, isLoading, isError, error, refetch } = useQuery<Paginated<AdminInvitation>>({
    queryKey: ['admin', 'invitations', 'byUser', userId],
    queryFn: () => listInvitations({ page: 1, limit: 50, search: userId }),
  });
  if (isLoading) return <TableSkeleton rows={4} cols={3} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  const items = data?.items.filter((i) => i.user.id === userId) ?? [];
  if (items.length === 0)
    return <EmptyState title="No invitations" hint="This user has not created any invitations yet." />;
  return (
    <ul className="space-y-2">
      {items.map((inv) => (
        <li key={inv.id} className="glass-panel rounded-lg p-3 flex justify-between gap-3">
          <div>
            <div className="font-bold text-sm">{inv.eventName}</div>
            <div className="text-[11px] text-on-surface-variant">{inv.occasionType}</div>
          </div>
          <div className="text-right text-[11px] text-on-surface-variant">
            {new Date(inv.eventDate).toLocaleDateString('en-IN')}
            <div className="uppercase">{inv.status}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}
